import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import {
  PACKAGE_DELAY_REASON_MAP,
  PACKAGE_PROBLEM_REASON_MAP,
} from '@returnmates/client-core/src/constants/trip'
import { PackageStatus } from '@returnmates/client-core/src/graphql/generated/api'
import {
  getBarcodeScan,
  packageConsolidationSelector,
} from '@returnmates/client-core/src/selectors/admin'
import { getCurrentPackage } from '@returnmates/client-core/src/selectors/packages'
import { memo, useEffect, useMemo } from 'react'
import { Field, useField } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import EditAddressFormInner from '../../EditAddressForm/EditAddressFormInner'
import ExpandableSection from '../../ExpandableSection'
import MediaData from '../../MediaData'
import PreviousCustomerInformation from '../../PreviousCustomerInformation'
import Select from '../../Select'
import { EXCLUDED_FROM_INVALIDS_PACKAGE_STATUSES } from '../constants'
import useStyles from '../styles'
import BarcodeScans from './BarcodeScans'
import CarrierInformation from './CarrierInformation'
import Ids from './Ids'
import Information from './Information'
import LocationScans from './LocationScans'
import PackageSource from './PackageSource'
import PackageStatusSelect from './PackageStatusSelect'
import StatusHistory from './StatusHistory'

interface Props {
  setIsCurrentPackageProblem: (val: boolean) => void
}

function EditPackageFormInner({ setIsCurrentPackageProblem }: Props) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const barcodeScan = useSelector(getBarcodeScan)
  const currentPackage = useSelector(getCurrentPackage)
  const packageConsolidation = useSelector(packageConsolidationSelector)
  const { input: consolidationIdInput } = useField('consolidationId')
  const { input: scans } = useField('scans')
  const { input: locationScans } = useField('locationScans')
  const { input: statusInput } = useField('status')

  const partnerAttributes = useMemo<Array<Array<string>>>(() => {
    if (currentPackage?.partnerAttributes) {
      let parsedAttributes = JSON.parse(currentPackage.partnerAttributes)

      if (typeof parsedAttributes === 'string') {
        parsedAttributes = JSON.parse(parsedAttributes)
      }

      if (Array.isArray(parsedAttributes)) {
        parsedAttributes = parsedAttributes.reduce((acc, obj) => Object.assign(acc, obj), {})
      }

      return Object.entries(parsedAttributes).map(attr => {
        return attr.map(entity => {
          if (typeof entity !== 'string') {
            return ''
          }
          return entity
        })
      })
    }

    return []
  }, [currentPackage?.partnerAttributes])

  const isShowPackageDelayReasonSelect = useMemo(
    () => statusInput.value === PackageStatus.DELAYED || currentPackage?.packageDelayReason,
    [statusInput.value, currentPackage?.packageDelayReason],
  )

  const isShowPackageProblemReasonSelect = useMemo(
    () => statusInput.value === PackageStatus.PROBLEM,
    [statusInput.value],
  )

  useEffect(() => {
    if (barcodeScan?.consolidationId || barcodeScan?.scannedId) {
      dispatch(
        actions.scanBarcode.request({
          consolidationId: null,
          scannedId: null,
        }),
      )
    }
  }, [barcodeScan?.scannedId, barcodeScan?.consolidationId, dispatch])

  useEffect(() => {
    if (packageConsolidation?.consolidationId !== consolidationIdInput.value) {
      consolidationIdInput.onChange(packageConsolidation?.consolidationId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setIsCurrentPackageProblem(!EXCLUDED_FROM_INVALIDS_PACKAGE_STATUSES.includes(statusInput.value))
  }, [setIsCurrentPackageProblem, statusInput.value])

  return (
    <>
      <Ids currentPackage={currentPackage} />

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Status</p>
        <PackageStatusSelect />
      </div>

      {isShowPackageDelayReasonSelect ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Package Delay Reason</p>
          <Field name="packageDelayReason">
            {({ input, meta }) => (
              <Select
                value={input.value}
                onChange={input.onChange}
                values={PACKAGE_DELAY_REASON_MAP}
                className={classes.selectField}
                wrapperClassName={classes.selectWrapper}
                error={meta.error}
                withEmpty
                emptyItemTitle="None"
                emptyItemValue=""
              />
            )}
          </Field>
        </div>
      ) : null}

      {isShowPackageProblemReasonSelect ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Package Problem Reason</p>
          <Field name="packageProblemReason">
            {({ input, meta }) => (
              <Select
                value={input.value}
                onChange={input.onChange}
                values={PACKAGE_PROBLEM_REASON_MAP}
                className={classes.selectField}
                wrapperClassName={classes.selectWrapper}
                error={meta.error}
              />
            )}
          </Field>
        </div>
      ) : null}

      <ExpandableSection isExpandedByDefault title="Customer Information">
        <EditAddressFormInner userId={currentPackage?.address?.userId} />
      </ExpandableSection>

      {currentPackage?.previousAddresses.length ? (
        <ExpandableSection title="Previous Customer Information">
          <PreviousCustomerInformation currentPackage={currentPackage} />
        </ExpandableSection>
      ) : null}

      <ExpandableSection title="Information">
        <Information currentPackage={currentPackage} />
      </ExpandableSection>

      <ExpandableSection title="Carrier Information">
        <CarrierInformation currentPackage={currentPackage} />
      </ExpandableSection>

      <ExpandableSection title="Media">
        <MediaData />
      </ExpandableSection>

      {partnerAttributes.length ? (
        <ExpandableSection title="Partner Attributes">
          {partnerAttributes.map(attribute => (
            <div key={attribute[0]} className={classes.detailsDataRow}>
              <div className={classes.detailsType}>{attribute[0]}</div>
              <div className={classes.detailsData}>{attribute[1]}</div>
            </div>
          ))}
        </ExpandableSection>
      ) : null}

      {scans.value.length ? (
        <ExpandableSection childrenType="table" title="Barcode Scans">
          <BarcodeScans />
        </ExpandableSection>
      ) : null}

      {locationScans.value.length ? (
        <ExpandableSection childrenType="table" title="Location Scans">
          <LocationScans />
        </ExpandableSection>
      ) : null}

      <ExpandableSection childrenType="table" title="Status History">
        <StatusHistory packageId={currentPackage?.id} />
      </ExpandableSection>

      {currentPackage?.source ? (
        <ExpandableSection childrenType="table" title="Package Source">
          <PackageSource currentPackage={currentPackage} />
        </ExpandableSection>
      ) : null}
    </>
  )
}

export default memo(EditPackageFormInner)
